import { useMemo, useRef } from 'react';

import { useSelectionBarControlWidths } from '~/components/v2/components/private-selection-bar/hooks/useSelectionBarControlWidths';
import {
  areSelectionBarLayoutTierFlagsEqual,
  buildScopeBandLayoutReserves,
  getCompactCtaThresholdPx,
  getScopeBandReservesFromWidths,
  getThumbThresholdPx,
  layoutTierFlags,
  SCOPE_BAND_REFERENCE_WIDTHS,
  type SelectionBarLayoutTierFlags,
  useSelectionBarLayoutTier,
} from '~/components/v2/components/private-selection-bar/hooks/useSelectionBarLayoutTier';

export type UseSelectionBarMeasuredLayoutTierParams = {
  canOfferSelectAll: boolean;
  canSelectAllInCurrentView: boolean;
  countLabel: string;
  hasScopeLabel: boolean;
  hasShowcaseShare: boolean;
  scopeLabel?: string;
  selectAllLabel: string;
  showItemCountChevron: boolean;
  willOfferSelectAll: boolean;
};

export const useSelectionBarMeasuredLayoutTier = ({
  canOfferSelectAll,
  canSelectAllInCurrentView,
  countLabel,
  hasScopeLabel,
  hasShowcaseShare,
  scopeLabel,
  selectAllLabel,
  showItemCountChevron,
  willOfferSelectAll,
}: UseSelectionBarMeasuredLayoutTierParams) => {
  const staticLayoutReserves = useMemo(
    () => ({
      compactCtaThresholdPx: getCompactCtaThresholdPx(hasShowcaseShare),
      thumbThresholdPx: getThumbThresholdPx(hasShowcaseShare),
    }),
    [hasShowcaseShare],
  );

  const { isPhone, measureRef, toolbarWidth } = useSelectionBarLayoutTier();

  const bootstrapScopeBandReserves = useMemo(
    () =>
      buildScopeBandLayoutReserves(
        getScopeBandReservesFromWidths(SCOPE_BAND_REFERENCE_WIDTHS, willOfferSelectAll, hasScopeLabel),
        willOfferSelectAll,
      ),
    [hasScopeLabel, willOfferSelectAll],
  );

  const bootstrapTierFlags = useMemo(
    () => layoutTierFlags(toolbarWidth, { ...staticLayoutReserves, ...bootstrapScopeBandReserves }, isPhone),
    [bootstrapScopeBandReserves, isPhone, staticLayoutReserves, toolbarWidth],
  );

  const committedTierFlagsRef = useRef<SelectionBarLayoutTierFlags>(bootstrapTierFlags);
  const committedTierFlags = committedTierFlagsRef.current;

  const provisionalShowSelectAll =
    canSelectAllInCurrentView && canOfferSelectAll && !committedTierFlags.hideSelectAllButton;

  const { controlMeasureRefs, scopeBandReserves } = useSelectionBarControlWidths({
    countLabel,
    hideScopeLabel: committedTierFlags.hideScopeLabel,
    scopeLabel: hasScopeLabel ? scopeLabel : undefined,
    selectAllLabel,
    showItemCountChevron,
    showSelectAll: provisionalShowSelectAll,
    willOfferSelectAll,
  });

  const scopeBandLayoutReserves = useMemo(
    () => buildScopeBandLayoutReserves(scopeBandReserves, willOfferSelectAll),
    [scopeBandReserves, willOfferSelectAll],
  );

  const tierFlags = useMemo(
    () =>
      layoutTierFlags(
        toolbarWidth,
        { ...staticLayoutReserves, ...scopeBandLayoutReserves },
        isPhone,
        committedTierFlags,
      ),
    [committedTierFlags, isPhone, scopeBandLayoutReserves, staticLayoutReserves, toolbarWidth],
  );

  if (!areSelectionBarLayoutTierFlagsEqual(committedTierFlagsRef.current, tierFlags)) {
    committedTierFlagsRef.current = tierFlags;
  }

  return {
    controlMeasureRefs,
    isPhone,
    measureRef,
    tierFlags,
  };
};
